import { useQuery } from '@apollo/client';
import { evolve } from 'ramda';
import React, {
  createContext,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Breakpoint } from '../../../BaseComponents/ThemeProvider/AvenyTheme';
import { ActiveDatabaseContext } from '../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { BusinessConfigContextProvider } from '../../../Components/BusinessConfigContext/BusinessConfigContext';
import {
  favouriteRoutineLayoutSettingValueName,
  userRoutineLayoutsSettingSectionName,
} from '../../../Components/KolumnValjare/Components/KolumnValjareDialog/Components/KolumnSettingSelect/KolumnSettingSelect';
import { getColumnWithCaption } from '../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialogUtils';
import { IAvenyModelContext } from '../../../Model/ModelContext/ModelContext';
import { IColumnDef } from '../../../Types/ColumnDefType';
import { IListLayout } from '../../../Types/ListLayoutTypes';
import { IRutinInfo } from '../../../Types/RutinInfoType';
import {
  dbSettingsInnehallValueName,
  nyaAvenyDBSettingsValueName,
  padRoutineId,
} from '../../../Utils/dbSettingsUtils';
import Logger from '../../../Utils/Logger';
import mergeSort, { stringCompareNumeric } from '../../../Utils/mergeSort';
import { getDefaultVisningsTyp } from '../../Delsystem/Utils/VisningsUtils';
import { rutiner } from '../../Delsystem/_Common/Const/Rutiner';
import { defaultColumns } from '../../Delsystem/_Common/Utils/defaultColumns';
import { defaultShowDetailsRight } from '../../Delsystem/_StandardRutin/StandardRutin';
import { getDefaultSortProperties } from '../../Delsystem/_StandardRutin/StandardRutinUtils';
import ActiveRoutineQueryDoc from './ActiveRoutineQuery.gql';
import {
  ActiveRoutineQuery,
  ActiveRoutineQueryVariables,
  ActiveRoutineQuery_me_routineLayouts_sections,
} from './_QueryTypes/ActiveRoutineQuery';
import { KodplanContext } from '../../../Components/KodplanManager/Context/KodplanContext';
import { RutinSettingsContext } from '../../Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import routineDefaultSelectionQueryDoc from './Queries/RoutineDefaultSelectionQuery.gql';
import {
  RoutineDefaultSelectionQuery,
  RoutineDefaultSelectionQuery_routinedefaultselection,
  RoutineDefaultSelectionQueryVariables,
} from './Queries/_QueryTypes/RoutineDefaultSelectionQuery';

interface IProps {
  rutinInfo: IRutinInfo;
  collectionName: string;
}

export interface IActiveRoutineContext {
  rutinInfo: IRutinInfo;
  favoritLayoutId?: string;
  sparadeLayouter: IListLayout[];
  selectedLayout: IListLayout | null;
  setSelectedLayout: (newLayout: IListLayout | null) => void;
  activeRoutineQueryLoading: boolean;
  showDetailsRightInitialized: boolean;
  setShowDetailsRightInitialized: (value: boolean) => void;
  columnsInitialized: boolean;
  setColumnsInitialized: (value: boolean) => void;
  sortPropertiesInitialized: boolean;
  setSortPropertiesInitialized: (value: boolean) => void;
  initialUrvalId: string | undefined;
}

const columnStrToColumnDef = (str: string): IColumnDef => {
  const [attributeDef, settingsStr] = str.split('=');
  const settings = settingsStr?.split('|') ?? [];
  const windowsSizeStr = settings.at(0);
  const windowsSize = windowsSizeStr
    ? Number.parseInt(windowsSizeStr, 10)
    : undefined;
  const caption = settings.at(1);
  return {
    attributeDef,
    windowsSize,
    caption,
  };
};

export const columnDefsToColumnStr = (
  columnDefs: readonly IColumnDef[]
): string => {
  return columnDefs
    .map(
      columnDef =>
        `${columnDef.attributeDef}=${columnDef.windowsSize}|${columnDef.caption}`
    )
    .join(';');
};

//översätt borttagna visningstyper till aktuell typ
const migrateVisningstyp = (visningstyp: string): string =>
  visningstyp === 'MuiXTabell' ? 'tabell' : visningstyp;

//Om vi tar bort möjliga inställningar för en routineLayout i nya Aveny
//så ska den här funktionen uppdatera sparade inställningar med gamla värden
const migrateOldSettings = (setttings: any): any =>
  evolve({ visningstyp: migrateVisningstyp }, setttings);

const routineLayoutSectionToListLayout =
  (collectionName: string, rutinKod: string) =>
  (section: ActiveRoutineQuery_me_routineLayouts_sections): IListLayout => {
    const vtInnehallValue = section.values?.find(
      section => section.name === dbSettingsInnehallValueName
    )?.value;
    const strValue =
      vtInnehallValue?.__typename === 'TEDMixedString'
        ? vtInnehallValue.asString ?? ''
        : '';

    const nyaAvenyValue = section.values?.find(
      value => value.name === nyaAvenyDBSettingsValueName
    )?.value;

    const nyaAvenyValueString =
      (nyaAvenyValue?.__typename === 'TEDMixedString' &&
        nyaAvenyValue.asString) ||
      null;

    let nyaAvenyValueParsed: any = {};
    try {
      nyaAvenyValueParsed = nyaAvenyValueString
        ? JSON.parse(nyaAvenyValueString)
        : {};
    } catch (e) {
      Logger.error(new Error('Could not parse nyaAvenyValue'), {
        nyaAvenyValueString: nyaAvenyValueString,
      });
    }
    nyaAvenyValueParsed = migrateOldSettings(nyaAvenyValueParsed);
    const columnDefs: IColumnDef[] =
      strValue.split(';')?.map(columnStrToColumnDef) ?? [];

    const columns = defaultColumns(collectionName, rutinKod, columnDefs);

    return {
      name: section.name ?? undefined,
      id: section.id,
      columns: columns,
      ...nyaAvenyValueParsed,
    };
  };

export const defaultRoutineLayoutId = 'DefaultRoutineLayout';
export const defaultRoutineLayoutName = 'Grundinställning';

export const getDefaultRoutineLayout = (
  collectionName: string,
  modelContext: IAvenyModelContext,
  rutinKod: string,
  showDetailsRightDisabledOnCurrentScreenSize: boolean,
  screenWidth: Breakpoint,
  inCard: boolean = false
): IListLayout => {
  const columns = defaultColumns(
    collectionName,
    rutinKod,
    undefined,
    screenWidth
  ).map(column => getColumnWithCaption(collectionName, modelContext, column));
  const showDetailsRight = defaultShowDetailsRight(
    rutinKod,
    showDetailsRightDisabledOnCurrentScreenSize
  );
  const visningstyp = getDefaultVisningsTyp(screenWidth, inCard, rutinKod);
  const sortering = getDefaultSortProperties(
    columns,
    collectionName,
    modelContext,
    rutinKod
  );
  return {
    id: defaultRoutineLayoutId,
    name: defaultRoutineLayoutName,
    columns,
    showDetailsRight,
    visningstyp,
    sortering,
  };
};

export const ActiveRoutineContext = createContext<IActiveRoutineContext | null>(
  null
);

export const useActiveRoutineContext = (): IActiveRoutineContext => {
  const context = useContext(ActiveRoutineContext);
  if (context == null) {
    throw new Error('activeroutinecontext saknas');
  }
  return context;
};

export const getActiveRoutineQueryVariables = (
  activeDatabase: string | null,
  activeRoutineId: number | undefined,
  collectionName: string,
  kontoplanId: string | undefined
): ActiveRoutineQueryVariables => {
  const routineFormSettingsPath = `Forms/${padRoutineId(activeRoutineId ?? 0)}`;
  return {
    dbName: activeDatabase ?? 'dbMissing',
    userRoutineLayoutsSection: `${routineFormSettingsPath}/${userRoutineLayoutsSettingSectionName}`,
    favouriteRoutineLayoutSection: routineFormSettingsPath,
  };
};

const ActiveRoutineContextProvider: FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const kodplanContext = useContext(KodplanContext);
  const rutinAvailabilityContext = useContext(RutinSettingsContext);
  const [selectedLayout, setSelectedLayout] = useState<IListLayout | null>(
    null
  );
  const [showDetailsRightInitialized, setShowDetailsRightInitialized] =
    useState(false);
  const [columnsInitialized, setColumnsInitialized] = useState(false);
  const [sortPropertiesInitialized, setSortPropertiesInitialized] =
    useState(false);
  const [favouriteSettingInitialized, setFavouriteSettingInitialized] =
    useState(false);
  const [userStartUrval, setUserStartUrval] = useState<
    RoutineDefaultSelectionQuery_routinedefaultselection | undefined | null
  >(null);

  const activeRoutineId = props.rutinInfo.rutinID;
  const skip = !activeDatabaseContext.activeDatabase || activeRoutineId == null;

  const { data, loading } = useQuery<
    ActiveRoutineQuery,
    ActiveRoutineQueryVariables
  >(ActiveRoutineQueryDoc, {
    variables: getActiveRoutineQueryVariables(
      activeDatabaseContext.activeDatabase,
      activeRoutineId,
      props.collectionName,
      kodplanContext.activeKodplan?.id
    ),
    skip,
  });

  let routineInitialUrval =
    rutiner[props.rutinInfo.rutinKod || '']?.initialUrval;
  if (rutinAvailabilityContext.hasRutinStartError) {
    routineInitialUrval = undefined;
  }

  const { loading: routineDefaultSelectionQueryLoading } = useQuery<
    RoutineDefaultSelectionQuery,
    RoutineDefaultSelectionQueryVariables
  >(routineDefaultSelectionQueryDoc, {
    variables: {
      dbName: activeDatabaseContext.activeDatabase ?? 'dbMissing',
      collection: props.collectionName,
      rutinID: activeRoutineId ?? 0,
      kontoplanID: kodplanContext.activeKodplan?.id,
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const userStartUrval = data?.routinedefaultselection?.anvFavorit
        ? data?.routinedefaultselection
        : undefined;
      setUserStartUrval(userStartUrval);
    },
    skip:
      Boolean(routineInitialUrval) || kodplanContext.activeKodplan?.id == null,
  });

  const favoritInstallningarIDValue =
    data?.me
      ?.at(0)
      ?.favoritRoutineLayout?.at(0)
      ?.values?.find(val => val.name === favouriteRoutineLayoutSettingValueName)
      ?.value ?? undefined;

  const favoritLayoutId =
    (favoritInstallningarIDValue &&
      favoritInstallningarIDValue.__typename === 'TEDMixedString' &&
      favoritInstallningarIDValue.asString) ||
    undefined;

  const initialUrvalId = routineInitialUrval ?? userStartUrval?.id;

  const sparadeLayouter: IListLayout[] = useMemo(
    () =>
      mergeSort(
        data?.me
          ?.at(0)
          ?.routineLayouts?.at(0)
          ?.sections?.map(
            routineLayoutSectionToListLayout(
              props.collectionName,
              props.rutinInfo.rutinKod
            )
          ) ?? [],
        (a, b) => {
          return stringCompareNumeric(a.name ?? '', b.name ?? '');
        }
      ),
    [data, props.collectionName, props.rutinInfo.rutinKod]
  );

  useEffect(() => {
    if (
      loading === false &&
      routineDefaultSelectionQueryLoading === false &&
      data &&
      !selectedLayout &&
      !favouriteSettingInitialized
    ) {
      if (favoritLayoutId) {
        const favouriteLayout = sparadeLayouter.find(
          savedLayout => savedLayout.id === favoritLayoutId
        );

        if (favouriteLayout) {
          setSelectedLayout(favouriteLayout);
        }
      }
      setFavouriteSettingInitialized(true);
    }
  }, [
    loading,
    data,
    favoritLayoutId,
    selectedLayout,
    favouriteSettingInitialized,
    sparadeLayouter,
    routineDefaultSelectionQueryLoading,
  ]);

  const value: IActiveRoutineContext = useMemo(
    () => ({
      rutinInfo: props.rutinInfo,
      favoritLayoutId: favoritLayoutId,
      sparadeLayouter,
      selectedLayout,
      setSelectedLayout,
      activeRoutineQueryLoading: loading || routineDefaultSelectionQueryLoading,
      showDetailsRightInitialized,
      setShowDetailsRightInitialized,
      columnsInitialized,
      setColumnsInitialized,
      sortPropertiesInitialized,
      setSortPropertiesInitialized,
      initialUrvalId,
    }),
    [
      props.rutinInfo,
      favoritLayoutId,
      sparadeLayouter,
      selectedLayout,
      setSelectedLayout,
      loading,
      showDetailsRightInitialized,
      setShowDetailsRightInitialized,
      columnsInitialized,
      setColumnsInitialized,
      sortPropertiesInitialized,
      setSortPropertiesInitialized,
      initialUrvalId,
      routineDefaultSelectionQueryLoading,
    ]
  );

  return (
    <ActiveRoutineContext.Provider value={value}>
      <BusinessConfigContextProvider
        value={rutiner[props.rutinInfo.rutinKod]?.config}
      >
        {props.children}
      </BusinessConfigContextProvider>
    </ActiveRoutineContext.Provider>
  );
};

export { ActiveRoutineContextProvider };
